import {useEffect, useMemo, useState} from 'react';
import {Layout} from '@/components/Layout';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Textarea} from '@/components/ui/textarea';
import {toast} from 'sonner';
import {Ban, Edit2, Plus, Search, Trash2} from 'lucide-react';
import {usePermissions} from '@/hooks/usePermissions';
import {api, getApiErrorMessage, tagDeactivationReasonEndpoints} from '@/lib/api';
import {PERMISSIONS} from '@/lib/permissions';

const emptyForm = {
    name: '',
    description: '',
    status: '1',
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const normalizeReason = (source = {}, fallback = {}) => ({
    ...fallback,
    ...source,
    id: source.id ?? source.tag_deactivation_reason_id ?? source.deactivation_reason_id ?? fallback.id,
    company_id: source.company_id ?? fallback.company_id ?? null,
    name: source.name ?? fallback.name ?? '',
    description: source.description ?? fallback.description ?? '',
    status: Number(source.status ?? fallback.status ?? 0),
});

const normalizeReasons = (payload) => {
    const reasons = collectionPayload(payload, 'tag_deactivation_reasons');
    const deactivationReasons = reasons.length ? reasons : collectionPayload(payload, 'deactivation_reasons');
    const namedReasons = deactivationReasons.length ? deactivationReasons : collectionPayload(payload, 'reasons');
    const items = namedReasons.length ? namedReasons : collectionPayload(payload, 'tagDeactivationReasons');
    return items.map((item, index) => normalizeReason(item, {id: index + 1}));
};

const reasonPayload = (form) => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    status: Number(form.status),
});

const SETTINGS_ACTION_PERMISSIONS = {
    create: PERMISSIONS.SETTINGS_CREATE,
    read: PERMISSIONS.SETTINGS_READ,
    update: PERMISSIONS.SETTINGS_UPDATE,
    delete: PERMISSIONS.SETTINGS_DELETE,
};

export const TagDeactivationReasonsPage = () => {
    const {can} = usePermissions();
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingReason, setEditingReason] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    const canReadSettings = can(SETTINGS_ACTION_PERMISSIONS.read);
    const canCreateSettings = can(SETTINGS_ACTION_PERMISSIONS.create);
    const canUpdateSettings = can(SETTINGS_ACTION_PERMISSIONS.update);
    const canDeleteSettings = can(SETTINGS_ACTION_PERMISSIONS.delete);
    const showActions = canUpdateSettings || canDeleteSettings;

    useEffect(() => {
        let cancelled = false;

        const loadReasons = async () => {
            if (!canReadSettings) {
                setReasons([]);
                setError('Nao tem permissão para consultar os motivos de desativação de tags.');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');

            try {
                const response = await api.get(tagDeactivationReasonEndpoints.list);
                if (!cancelled) setReasons(normalizeReasons(response.data));
            } catch (requestError) {
                if (!cancelled) {
                    setReasons([]);
                    setError(getApiErrorMessage(requestError, 'Nao foi possível carregar os motivos de desativação de tags.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadReasons();

        return () => {
            cancelled = true;
        };
    }, [canReadSettings]);

    const filteredReasons = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return reasons;
        return reasons.filter(reason =>
            [reason.name, reason.description]
                .filter(Boolean)
                .some(value => String(value).toLowerCase().includes(q))
        );
    }, [reasons, search]);

    const openCreate = () => {
        if (!canCreateSettings) {
            toast.error('Nao tem permissão para criar motivos de desativação de tags.');
            return;
        }

        setEditingReason(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (reason) => {
        if (!canUpdateSettings) {
            toast.error('Nao tem permissão para editar motivos de desativação de tags.');
            return;
        }

        setEditingReason(reason);
        setForm({
            name: reason.name || '',
            description: reason.description || '',
            status: String(reason.status ?? 0),
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (editingReason && !canUpdateSettings) {
            toast.error('Nao tem permissão para editar motivos de desativação de tags.');
            return;
        }

        if (!editingReason && !canCreateSettings) {
            toast.error('Nao tem permissão para criar motivos de desativação de tags.');
            return;
        }

        if (!form.name.trim()) {
            toast.error('Nome é obrigatório.');
            return;
        }

        setSubmitting(true);
        try {
            if (editingReason) {
                const response = await api.post(tagDeactivationReasonEndpoints.update, {
                    id: editingReason.id,
                    ...reasonPayload(form),
                });
                const updated = normalizeReason(response.data?.data ?? response.data?.tag_deactivation_reason ?? response.data?.reason ?? response.data, {
                    ...editingReason,
                    ...reasonPayload(form),
                });
                setReasons(prev => prev.map(reason => reason.id === editingReason.id ? updated : reason));
                toast.success(`Motivo "${updated.name}" atualizado.`);
            } else {
                const response = await api.post(tagDeactivationReasonEndpoints.create, reasonPayload(form));
                const created = normalizeReason(response.data?.data ?? response.data?.tag_deactivation_reason ?? response.data?.reason ?? response.data, {
                    id: Date.now(),
                    ...reasonPayload(form),
                });
                setReasons(prev => [created, ...prev]);
                toast.success(`Motivo "${created.name}" criado.`);
            }
            setDialogOpen(false);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Nao foi possível guardar o motivo de desativação de tags.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (reason) => {
        if (!canDeleteSettings) {
            toast.error('Nao tem permissão para eliminar motivos de desativação de tags.');
            return;
        }

        if (!window.confirm(`Eliminar motivo "${reason.name}"?`)) return;

        setSubmitting(true);
        try {
            await api.post(tagDeactivationReasonEndpoints.delete, {id: reason.id});
            setReasons(prev => prev.filter(item => item.id !== reason.id));
            toast.success(`Motivo "${reason.name}" removido.`);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Nao foi possível remover o motivo de desativação de tags.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Motivos de Desativacao de Tags</h1>
                        <p className="text-slate-600 font-primary mt-1">Gerir motivos usados ao marcar tags como inativas.</p>
                    </div>
                    {canCreateSettings && (
                        <Button onClick={openCreate} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="create-tag-deactivation-reason">
                            <Plus size={18} className="mr-2"/>
                            Novo Motivo
                        </Button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Total</p>
                            <Ban size={18} className="text-blue-600"/>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{reasons.length}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Ativos</p>
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">status 1</Badge>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{reasons.filter(reason => Number(reason.status) === 1).length}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Inativos</p>
                            <Badge className="bg-slate-100 text-slate-700 border-0">status 0</Badge>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{reasons.filter(reason => Number(reason.status) !== 1).length}</p>
                    </Card>
                </div>

                <div className="relative max-w-md">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <Input
                        className="pl-9"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder="Pesquisar por nome ou descricao ..."
                        data-testid="tag-deactivation-reason-search"
                    />
                </div>

                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Motivo</TableHead>
                                <TableHead>Estado</TableHead>
                                {showActions && <TableHead className="text-right">Ações</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(loading || error || filteredReasons.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={showActions ? 3 : 2} className="text-center text-sm text-slate-400 italic py-10">
                                        {loading ? 'A carregar motivos...' : error || 'Sem motivos para mostrar.'}
                                    </TableCell>
                                </TableRow>
                            ) : filteredReasons.map(reason => (
                                <TableRow key={reason.id} data-testid={`tag-deactivation-reason-row-${reason.id}`}>
                                    <TableCell>
                                        <p className="font-medium text-slate-900">{reason.name}</p>
                                        {reason.description && <p className="text-xs text-slate-500 line-clamp-1">{reason.description}</p>}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={Number(reason.status) === 1 ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-slate-100 text-slate-700 border-0'}>
                                            {Number(reason.status) === 1 ? 'Ativo' : 'Inativo'}
                                        </Badge>
                                    </TableCell>
                                    {showActions && (
                                        <TableCell className="text-right">
                                            {canUpdateSettings && (
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(reason)} disabled={submitting} data-testid={`edit-tag-deactivation-reason-${reason.id}`}>
                                                    <Edit2 size={14} className="text-slate-600"/>
                                                </Button>
                                            )}
                                            {canDeleteSettings && (
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(reason)} disabled={submitting} data-testid={`delete-tag-deactivation-reason-${reason.id}`}>
                                                    <Trash2 size={14} className="text-red-500"/>
                                                </Button>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-xl" data-testid="tag-deactivation-reason-dialog">
                        <DialogHeader>
                            <DialogTitle>{editingReason ? 'Editar Motivo de Desativação' : 'Novo Motivo de Desativação'}</DialogTitle>
                            <DialogDescription>Configurar nome, descricao e estado do motivo.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="space-y-2">
                                <Label>Nome *</Label>
                                <Input value={form.name} onChange={event => setForm(prev => ({...prev, name: event.target.value}))} data-testid="tag-deactivation-reason-name"/>
                            </div>
                            <div className="space-y-2">
                                <Label>Descricao</Label>
                                <Textarea rows={3} value={form.description} onChange={event => setForm(prev => ({...prev, description: event.target.value}))} data-testid="tag-deactivation-reason-description"/>
                            </div>
                            <div className="space-y-2">
                                <Label>Estado</Label>
                                <Select value={form.status} onValueChange={value => setForm(prev => ({...prev, status: value}))}>
                                    <SelectTrigger data-testid="tag-deactivation-reason-status"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">Ativo</SelectItem>
                                        <SelectItem value="0">Inativo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>Cancelar</Button>
                            <Button onClick={handleSubmit} disabled={submitting || (editingReason ? !canUpdateSettings : !canCreateSettings)} className="bg-blue-600 hover:bg-blue-700" data-testid="tag-deactivation-reason-submit">
                                {submitting ? 'A guardar...' : editingReason ? 'Guardar' : 'Criar Motivo'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};
